import { useEffect, useState, type FormEvent } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiFehler } from '../lib/api'
import { Fenster } from './Fenster'
import { useTeststand } from '../lib/test'

interface Kanzlei {
  name: string
  loeschfrist_tage: number
  camt_version: '02' | '08'
}

/** Was eine Kanzlei selbst einstellt: wie lange Auszuege bleiben, welche camt-Version neue Konten bekommen. */
export function Einstellungen({ offen, aufSchliessen }: { offen: boolean; aufSchliessen: () => void }) {
  const abfragen = useQueryClient()
  const { stand: teststand } = useTeststand()
  const { data } = useQuery({ queryKey: ['kanzlei'], queryFn: () => api.holen<{ kanzlei: Kanzlei }>('/api/kanzlei'), enabled: offen })
  const [frist, setzeFrist] = useState('')
  const [version, setzeVersion] = useState<'02' | '08'>('08')
  const [fehler, setzeFehler] = useState<string | null>(null)
  const [fertig, setzeFertig] = useState(false)
  const [laeuft, setzeLaeuft] = useState(false)

  useEffect(() => {
    if (data) {
      setzeFrist(String(data.kanzlei.loeschfrist_tage))
      setzeVersion(data.kanzlei.camt_version)
    }
  }, [data])

  function schliessen() {
    setzeFehler(null)
    setzeFertig(false)
    aufSchliessen()
  }

  async function absenden(e: FormEvent) {
    e.preventDefault()
    setzeFehler(null)
    setzeLaeuft(true)
    try {
      await api.aendern('/api/kanzlei', { loeschfrist_tage: Number(frist), camt_version: version })
      await abfragen.invalidateQueries({ queryKey: ['kanzlei'] })
      setzeFertig(true)
    } catch (problem) {
      setzeFehler(problem instanceof ApiFehler ? problem.message : 'Die Einstellungen konnten nicht gespeichert werden.')
    } finally {
      setzeLaeuft(false)
    }
  }

  return (
    <Fenster titel="Einstellungen der Kanzlei" offen={offen} aufSchliessen={schliessen}>
      <form className="formular" onSubmit={(e) => void absenden(e)}>
        {fehler && <div className="fehler">{fehler}</div>}
        {fertig && <div className="bestaetigung">Gespeichert.</div>}

        <div>
          <label htmlFor="e-frist">Löschfrist nach dem Export</label>
          <input id="e-frist" type="number" min={7} max={3650} required value={frist} onChange={(e) => setzeFrist(e.target.value)} />
          <div className="hinweis">
            Tage. Danach werden Original, gelesene Buchungen und Exportdatei gelöscht. Was nie exportiert wurde, ebenfalls.
          </div>
        </div>

        {!teststand.test && (
          <div>
            <label>camt-Version für neue Konten</label>
            <div className="wahl">
              <button type="button" aria-pressed={version === '08'} onClick={() => setzeVersion('08')}>camt.053.001.08</button>
              <button type="button" aria-pressed={version === '02'} onClick={() => setzeVersion('02')}>camt.053.001.02</button>
            </div>
            <div className="hinweis">
              DATEV nimmt beide an, verlangt aber je Konto immer dieselbe. Ein Konto, das schon exportiert wurde, behält seine Version. .08 ist der aktuelle Standard der Banken.
            </div>
          </div>
        )}

        <div className="formular-fuss">
          <button type="button" className="knopf still" onClick={schliessen}>
            {fertig ? 'Schließen' : 'Abbrechen'}
          </button>
          <button type="submit" className="knopf gefuellt" disabled={laeuft || !data}>
            {laeuft ? 'Wird gespeichert …' : 'Speichern'}
          </button>
        </div>
      </form>
    </Fenster>
  )
}
